import {useI18n} from '@/i18n'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Checkbox} from '@/components/ui/checkbox'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import type {DesignerAction, LayoutSchema} from '@/types/form-layout'

interface SectionPropertiesProps {
    schema: LayoutSchema
    sectionKey: string
    dispatch: React.Dispatch<DesignerAction>
}

export function SectionProperties({schema, sectionKey, dispatch}: SectionPropertiesProps) {
    const i18n = useI18n()
    const section = schema.sections.find((s) => s.key === sectionKey)

    if (!section) return null

    return (
        <div className="space-y-4">
            {/* Label */}
            <div className="space-y-1.5">
                <Label htmlFor="section-label">{i18n.formLayouts.sectionLabel}</Label>
                <Input
                    id="section-label"
                    value={section.label ?? ''}
                    onChange={(e) =>
                        dispatch({type: 'UPDATE_SECTION', sectionKey, patch: {label: e.target.value}})
                    }
                    placeholder={i18n.formLayouts.sectionLabelPlaceholder}
                />
                {!(section.label ?? '').trim() && (
                    <p className="text-xs text-destructive">{i18n.formLayouts.sectionLabelRequired}</p>
                )}
            </div>

            {/* Columns */}
            <div className="space-y-1.5">
                <Label htmlFor="section-columns">{i18n.formLayouts.sectionColumns}</Label>
                <Select
                    value={String(section.columns)}
                    onValueChange={(val) =>
                        dispatch({
                            type: 'UPDATE_SECTION',
                            sectionKey,
                            patch: {columns: Number(val) as 1 | 2 | 3},
                        })
                    }
                >
                    <SelectTrigger data-testid="section-columns-prop-select" id="section-columns">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="1">{i18n.formLayouts.columns1}</SelectItem>
                        <SelectItem value="2">{i18n.formLayouts.columns2}</SelectItem>
                        <SelectItem value="3">{i18n.formLayouts.columns3}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Collapsible */}
            <div className="flex items-center gap-2">
                <Checkbox
                    id="section-collapsible"
                    checked={section.collapsible}
                    onCheckedChange={(checked) =>
                        dispatch({type: 'UPDATE_SECTION', sectionKey, patch: {collapsible: !!checked}})
                    }
                />
                <Label htmlFor="section-collapsible" className="cursor-pointer">
                    {i18n.formLayouts.sectionCollapsible}
                </Label>
            </div>
        </div>
    )
}
